import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { 
  ReactFlow,
  Node, 
  Edge, 
  Controls, 
  Background, 
  MiniMap,
  ReactFlowProvider,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  BackgroundVariant,
  useReactFlow,
  OnNodeDrag
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { observer } from 'mobx-react-lite';
import { reaction } from 'mobx';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import WorkflowNode from './NodeTypes/WorkflowNode';
import EnhancedWorkflowNode from './NodeTypes/EnhancedWorkflowNode';
import WorkflowEdge from './EdgeTypes/WorkflowEdge';
import { useMindmapLayout, triggerLayout } from '../../hooks/useMindmapLayout';
import { useProximityReparenting } from '../../hooks/useProximityReparenting';
import Cursors from './Cursors';


// Custom node component for root nodes
const RootNode = ({ data }: { data: any }) => {
  return (
    <div style={{
      padding: '12px 24px',
      borderRadius: '12px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: '2px solid #4c51bf',
      fontWeight: 'bold',
      minWidth: '180px',
      textAlign: 'center',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
    }}>
      <Handle type="target" position={Position.Left} style={{ visibility: 'hidden' }} />
      <div style={{ fontSize: '16px' }}>{data.label}</div>
      <Handle type="source" position={Position.Right} style={{
        width: '10px',
        height: '10px',
        backgroundColor: '#ffffff',
        border: '2px solid #4c51bf'
      }} />
    </div>
  );
};

interface MindmapProps {
  nodes?: Node[];
  edges?: Edge[];
  onNodesChange?: (changes: any) => void;
  onEdgesChange?: (changes: any) => void;
  onConnect?: (connection: any) => void;
  autoLayout?: boolean;
  onNodeDoubleClick?: (node: Node) => void;
  onNodeExpand?: (nodeId: string) => void;
  selectedNodeId?: string | null;
}

// Inner component that will be wrapped in ReactFlowProvider
const MindmapInner: React.FC<MindmapProps & { autoLayout: boolean }> = observer(({ 
  nodes: propNodes, 
  edges: propEdges,
  onNodesChange: propsOnNodesChange,
  onEdgesChange: propsOnEdgesChange,
  onConnect,
  autoLayout,
  onNodeDoubleClick: externalNodeDoubleClick,
  onNodeExpand: externalNodeExpand,
  selectedNodeId
}) => {
  const appStore = useAppStore();
  const { mindmap, mindmapCursor, workspaceRealtime, auth, mindmapViewport } = appStore;
  const reactFlowInstance = useReactFlow();
  const { screenToFlowPosition, setViewport } = reactFlowInstance;
  
  // Use React Flow's internal state management
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  // Cursor tracking: throttled mouse move handler
  const lastCursorSendRef = React.useRef(0);
  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (!workspaceRealtime.isConnected || !mindmap.mindmapId) return;
    
    const now = Date.now();
    // Throttle to 20ms (50fps max) client-side
    if (now - lastCursorSendRef.current < 20) return;
    lastCursorSendRef.current = now;
    
    try {
      const flowPosition = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      workspaceRealtime.sendCursorUpdate(mindmap.mindmapId, flowPosition.x, flowPosition.y);
    } catch (error) {
      // Silently ignore cursor errors
    }
  }, [workspaceRealtime, mindmap.mindmapId, screenToFlowPosition]);
  
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasPerformedInitialFitView, setHasPerformedInitialFitView] = useState(false);
  
  // Proximity-based re-parenting
  const {
    handleNodeDragStart,
    handleNodeDrag,
    handleNodeDragStop,
    modifiedEdges,
    isNodeBeingDragged,
    isClosestNode,
  } = useProximityReparenting(nodes, edges, {
    threshold: 200, // pixels
    showDraftEdges: true,
  });
  
  // Load saved viewport when mindmap changes
  useEffect(() => {
    if (mindmap.mindmapId) {
      const savedViewport = mindmapViewport.getViewport(mindmap.mindmapId);
      if (savedViewport) {
        console.log('[Mindmap] Restoring saved viewport:', savedViewport);
        setViewport(savedViewport, { duration: 300 });
      }
    }
  }, [mindmap.mindmapId, mindmapViewport, setViewport]);
  
  // Initialize nodes and edges from store ONCE
  useEffect(() => {
    if (!isInitialized && mindmap.nodes.length > 0) {
      console.log('[Mindmap] Initializing with nodes from store:', mindmap.nodes.length);
      const initialNodes = mindmap.reactFlowNodes;
      const initialEdges = mindmap.reactFlowEdges;
      setNodes(initialNodes);
      setEdges(initialEdges);
      setIsInitialized(true);
    }
  }, [mindmap.nodes.length, isInitialized, setNodes, setEdges]);
  
  // Perform fitView ONCE after initial load
  useEffect(() => {
    if (isInitialized && !hasPerformedInitialFitView && nodes.length > 0) {
      console.log('[Mindmap] Performing initial fitView');
      setTimeout(() => {
        reactFlowInstance.fitView({ 
          padding: 0.3,
          minZoom: 0.5,
          maxZoom: 2.0,
          duration: 300 
        });
        setHasPerformedInitialFitView(true);
      }, 100);
    }
  }, [isInitialized, hasPerformedInitialFitView, nodes.length, reactFlowInstance]);
  
  // Apply patches when store changes (after initialization)
  useEffect(() => {
    if (!isInitialized) return;
    
    // This is where we'd apply only the differences
    // For now, let's track the previous state and apply changes
    const handleStoreChange = () => {
      const storeNodes = mindmap.reactFlowNodes;
      const storeEdges = mindmap.reactFlowEdges;
      
      // Apply node changes
      setNodes((currentNodes) => {
        const nodeMap = new Map(currentNodes.map(n => [n.id, n]));
        
        // First, handle nodes that were removed from store (e.g., temp nodes replaced)
        const storeNodeIds = new Set(storeNodes.map(n => n.id));
        const tempNodesToRemove = currentNodes.filter(n => 
          n.id.startsWith('temp-') && !storeNodeIds.has(n.id)
        );
        
        console.log('🔄 [Mindmap] Updating nodes:', {
          current: currentNodes.length,
          fromStore: storeNodes.length,
          tempToRemove: tempNodesToRemove.length
        });
        
        const updatedNodes = storeNodes.map(storeNode => {
          // Add onExpand handler to node data
          const nodeWithHandlers = {
            ...storeNode,
            data: {
              ...storeNode.data,
              onExpand: externalNodeExpand,
            },
          };
          
          const existing = nodeMap.get(nodeWithHandlers.id);
          
          // Check if this might be replacing a temp node
          const tempNode = currentNodes.find(n => 
            n.id.startsWith('temp-') && 
            n.data?.label === nodeWithHandlers.data?.label &&
            n.data?.parentId === nodeWithHandlers.data?.parentId
          );
          
          if (tempNode && !existing) {
            console.log('🔄 [Mindmap] Replacing temp node with real node:', {
              tempId: tempNode.id,
              realId: nodeWithHandlers.id,
              preservingPosition: tempNode.position
            });
            // Preserve the temp node's position
            return {
              ...nodeWithHandlers,
              position: tempNode.position
            };
          }
          
          if (existing && JSON.stringify(existing.data) === JSON.stringify(nodeWithHandlers.data)) {
            // No change, return existing to prevent re-render
            return existing;
          }
          
          if (existing) {
            // Node data changed but preserve position
            console.log('🔄 [Mindmap] Node data changed, preserving position:', {
              id: nodeWithHandlers.id,
              position: existing.position
            });
            return {
              ...nodeWithHandlers,
              position: existing.position
            };
          }
          
          // New node
          return nodeWithHandlers;
        });
        
        return updatedNodes;
      });
      
      // Apply edge changes
      setEdges(storeEdges);
    };
    
    // Subscribe to store changes - track hierarchy, priority, necessity, and metadata
    const disposer = reaction(
      () => ({ 
        nodeCount: mindmap.nodes.length,
        // Create a stable hash that includes all properties that affect rendering
        // Including priority and isNecessary so edge colors and node appearance update
        nodeHash: mindmap.nodes.map(n => 
          `${n.id}:${n.parentId}:${n.priority}:${n.isNecessary}`
        ).sort().join('|'),
        // Track metadata changes too so badges update when data loads
        commentsCount: mindmap.comments.length,
        githubLinksCount: mindmap.githubLinks.length,
      }),
      handleStoreChange,
      { 
        fireImmediately: true, // Fire immediately on subscription
        delay: 50 // Small delay to batch rapid changes
      }
    );
    
    return () => disposer();
  }, [isInitialized, mindmap, setNodes, setEdges]);
  
  // Apply layout when enabled
  useMindmapLayout(autoLayout && isInitialized);
  
  // Define custom node types
  const nodeTypes = useMemo(() => ({
    root: EnhancedWorkflowNode,  // Use EnhancedWorkflowNode for all types
    default: EnhancedWorkflowNode,
    workflow: EnhancedWorkflowNode,
    'enhanced-workflow': EnhancedWorkflowNode,
  }), []);
  
  // Define custom edge types
  const edgeTypes = useMemo(() => ({
    workflow: WorkflowEdge,
    default: WorkflowEdge,  // Use WorkflowEdge as default too
  }), []);
  
  // Handle node selection (single click - select only, CMD/Ctrl+Click opens sidebar)
  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
    
    // Check for CMD (Mac) or Ctrl (Windows/Linux) key
    const isModifierKeyPressed = event.metaKey || event.ctrlKey;
    
    if (isModifierKeyPressed) {
      // CMD/Ctrl+Click: Open sidebar with this node
      console.log('CMD/Ctrl+Click detected - opening sidebar for node:', node.id);
      mindmap.openSidebar(node.id);
    } else {
      // Regular click: Just select the node
      mindmap.selectNode(node.id);
      console.log('Node clicked (selected only):', node.id);
    }
  }, [mindmap]);
  
  // Handle node double-click - DISABLED 
  const handleNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node) => {
    // Double-click does nothing - single click selects node only
    console.log('Double-click disabled');
  }, []);
  
  // Sync selected node with external selectedNodeId prop
  useEffect(() => {
    if (selectedNodeId) {
      const node = nodes.find(n => n.id === selectedNodeId);
      if (node) {
        setSelectedNode(node);
      }
    } else {
      setSelectedNode(null);
    }
  }, [selectedNodeId, nodes]);
  
  /**
   * Handle node drag end with re-parenting logic
   */
  const onNodeDragStop = useCallback<OnNodeDrag>(async (event, node) => {
    const result = handleNodeDragStop(event as React.MouseEvent, node);
    
    console.log('🎯 [Mindmap] Node drag stopped:', {
      nodeId: node.id,
      shouldReparent: result.shouldReparent,
      newParentId: result.newParentId,
      movedSignificantly: result.movedSignificantly,
    });
    
    // If we should re-parent, call the store action
    if (result.shouldReparent && result.newParentId) {
      console.log(`🔄 [Mindmap] Re-parenting ${node.id} to ${result.newParentId}`);
      try {
        await mindmap.reparentNodeOptimistically(node.id, result.newParentId);
        
        // After successful reparenting, trigger layout recalculation
        console.log('🎨 [Mindmap] Triggering layout recalculation after reparenting');
        setTimeout(() => {
          triggerLayout();
        }, 150); // Small delay to let the state update propagate
      } catch (error) {
        console.error('❌ [Mindmap] Failed to reparent node:', error);
      }
    } else if (result.movedSignificantly && !result.shouldReparent) {
      console.log(`↩️ [Mindmap] Node moved but not close enough for re-parenting`);
    }
  }, [handleNodeDragStop, mindmap]);
  
  /**
   * Handle viewport movement end - save viewport position
   * Only saves when movement stops (not during drag) to avoid thousands of updates
   */
  const handleMoveEnd = useCallback((_event: any, viewport: { x: number; y: number; zoom: number }) => {
    if (mindmap.mindmapId) {
      console.log('[Mindmap] Viewport movement ended, saving:', viewport);
      mindmapViewport.setViewport(mindmap.mindmapId, viewport.x, viewport.y, viewport.zoom);
    }
  }, [mindmap.mindmapId, mindmapViewport]);
  
  /**
   * Enhanced nodes with visual feedback for dragging/proximity
   */
  const enhancedNodes = useMemo(() => {
    return nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        isBeingDragged: isNodeBeingDragged(node.id),
        isClosestToTarget: isClosestNode(node.id),
        onExpand: (nodeId: string) => {
          if (externalNodeExpand) {
            externalNodeExpand(nodeId);
          }
        },
      },
      style: {
        ...node.style,
        // Highlight the closest node with a green glow
        ...(isClosestNode(node.id) ? {
          boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.5)',
          transition: 'box-shadow 0.2s ease',
        } : {}),
        // Dim the dragged node slightly
        ...(isNodeBeingDragged(node.id) ? {
          opacity: 0.7,
        } : {}),
      },
    }));
  }, [nodes, isNodeBeingDragged, isClosestNode, externalNodeExpand]);

  // Get active cursors for rendering
  const activeCursors = mindmapCursor?.activeCursors || [];
  const cursorData = activeCursors.map(cursor => ({
    id: cursor.userId,
    color: cursor.userColor,
    x: cursor.x,
    y: cursor.y,
    name: cursor.userName,
    isLocalUser: cursor.userId === auth.user?.id,
  }));

  return (
    <div 
      style={{ width: '100%', height: '100%', position: 'relative' }}
      onMouseMove={handleMouseMove}
    >
      <ReactFlow
        nodes={enhancedNodes}
        edges={modifiedEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onNodeDragStart={handleNodeDragStart as OnNodeDrag}
        onNodeDrag={handleNodeDrag as OnNodeDrag}
        onNodeDragStop={onNodeDragStop}
        onMoveEnd={handleMoveEnd}
        defaultEdgeOptions={{
          type: 'workflow',
          animated: false
        }}
        nodesDraggable={true}
        nodesConnectable={false}
        deleteKeyCode={null}
      >
        <Background 
          variant={BackgroundVariant.Dots}
          gap={12}
          size={1}
          color="#e5e7eb"
        />
        <Controls style={{ bottom: '120px' }} />
        <MiniMap 
          nodeColor={(node) => {
            if (node.type === 'root') return '#667eea';
            if (node.data?.priority === 'high') return '#dc2626';
            if (node.data?.priority === 'low') return '#10b981';
            return '#6b7280';
          }}
        />
        {/* Render collaborative cursors */}
        <Cursors cursors={cursorData} />
      </ReactFlow>
    </div>
  );
});

// Wrapper component that provides ReactFlowProvider
const Mindmap: React.FC<MindmapProps> = (props) => {
  return (
    <ReactFlowProvider>
      <MindmapInner {...props} autoLayout={props.autoLayout ?? true} />
    </ReactFlowProvider>
  );
};

export default Mindmap;
